import React, { useContext, useMemo, useState } from 'react';
import values from 'lodash/values';
import orderBy from 'lodash/orderBy';
import sumBy from 'lodash/sumBy';

import { Box, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import RouteWrapper from './RouteWrapper';
import DataTableToolbarWithExportAndSearch from '../components/shared/DataTableToolbarWithExportAndSearch';

import { DataContext } from '../components/data/DataContext';
import { DataContextType } from '../components/data_types/interfaces';
import { CourseRow, HoleRow, MIN_RANKED_HOLE_PLAYS, getScoreDistribution, rankableHoles, toCourseRows, toHoleRows } from '../components/data/hole_insights';
import { DS, Meter, Overline, PageHeader, Panel, enterSx, formatSigned, numericSx, trendColor } from '../components/shared/Dashboard';
import { BUCKET_COLORS, CourseScale, DistributionBar, HoleDialog, formatToPar } from '../components/shared/HoleComponents';

/*****************************************************************************
 ** Small building blocks
*****************************************************************************/

const SignedCell: React.FC<{ value: number; digits?: number; invert?: boolean }> = ({ value, digits = 2, invert }) => (
    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: trendColor(invert ? -value : value), ...numericSx }}>
        {formatSigned(value, digits)}
    </Typography>
);

const CompareRow: React.FC<{ label: string; better: number; worse: number; color: string; betterText: string }> = ({ label, better, worse, color, betterText }) => {
    const total = better + worse;
    return (
        <Box sx={{ '& + &': { mt: 2.25 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text }}>{label}</Typography>
                <Typography sx={{ fontSize: '13px', color: DS.textMuted, ...numericSx }}>
                    <strong style={{ color: DS.text }}>{better}</strong> of {total} holes
                </Typography>
            </Box>
            <Meter value={total ? better / total : 0} color={color} height={8} />
            <Typography sx={{ fontSize: '11px', color: DS.textFaint, mt: 0.5, ...numericSx }}>
                {betterText} · {worse} worse
            </Typography>
        </Box>
    );
};

const HoleList: React.FC<{ title: string; subtitle: string; holes: HoleRow[]; onOpen: (h: HoleRow) => void; delay: number }> = ({ title, subtitle, holes, onOpen, delay }) => (
    <Panel title={title} subtitle={subtitle} grid={{ xs: 12, md: 6 }} delay={delay}>
        {holes.length === 0 ? (
            <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>You haven't played any holes yet.</Typography>
        ) : holes.map((h, i) => (
            <Box
                key={h.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(h)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen(h); }}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '18px 1fr auto auto',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    px: 1,
                    mx: -1,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    '&:hover': { background: DS.inset },
                }}
            >
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: DS.textFaint, ...numericSx }}>{i + 1}</Typography>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.course} <span style={{ color: DS.textMuted }}>#{h.holeNum}</span>
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: DS.textFaint, ...numericSx }}>Par {h.par} · {h.timesPlayed} play{h.timesPlayed !== 1 ? 's' : ''}</Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: DS.textMuted, textAlign: 'right', ...numericSx }}>
                    <strong style={{ color: DS.text }}>{h.myAvg}</strong> vs {h.field_avg}
                </Typography>
                <Box sx={{
                    minWidth: 56,
                    textAlign: 'center',
                    px: 1,
                    py: '2px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: trendColor(h.vsField),
                    background: alpha(trendColor(h.vsField), 0.14),
                    ...numericSx,
                }}>
                    {formatSigned(h.vsField, 2)}
                </Box>
            </Box>
        ))}
    </Panel>
);

const CourseCard: React.FC<{ course: CourseRow; delay: number }> = ({ course, delay }) => (
    <Box sx={{ p: 2, borderRadius: '12px', background: DS.inset, border: `1px solid ${DS.border}`, ...enterSx(delay) }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: DS.text }}>{course.name}</Typography>
                <Typography sx={{ fontSize: '11px', color: DS.textFaint, ...numericSx }}>Par {course.par} · Elite {course.elite}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 800, color: trendColor(course.vsField), lineHeight: 1.1, ...numericSx }}>
                    {formatSigned(course.vsField, 1)}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: DS.textMuted }}>vs field</Typography>
            </Box>
        </Box>
        <CourseScale course={course} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, fontSize: '12px', color: DS.textMuted, ...numericSx }}>
            <span>You <strong style={{ color: DS.text }}>{formatToPar(course.my_score)}</strong></span>
            <span>Field {formatToPar(course.field_score)}</span>
            <span>Elite {formatToPar(course.elite_score)}</span>
        </Box>
        <Typography sx={{ fontSize: '11px', color: DS.textFaint, mt: 0.75, ...numericSx }}>
            {course.vsElite >= 0 ? `${formatSigned(course.vsElite, 1)} ahead of elite` : `${Math.abs(course.vsElite).toFixed(1)} strokes behind elite`}
        </Typography>
    </Box>
);

/*****************************************************************************
 ** All holes table
*****************************************************************************/

const columns: GridColDef<HoleRow>[] = [
    {
        field: 'fullName',
        headerName: 'Hole',
        headerClassName: 'ho-header',
        flex: 1,
        minWidth: 170,
        renderCell: ({ row }) => (
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text }}>
                {row.course} <span style={{ color: DS.textMuted }}>#{row.holeNum}</span>
            </Typography>
        ),
    },
    { field: 'course', headerName: 'Course', headerClassName: 'ho-header', width: 150 },
    { field: 'timesPlayed', type: 'number', headerName: 'Plays', headerClassName: 'ho-header', width: 80 },
    { field: 'par', type: 'number', headerName: 'Par', headerClassName: 'ho-header', width: 65 },
    { field: 'elite', type: 'number', headerName: 'Elite', headerClassName: 'ho-header', width: 70 },
    {
        field: 'myAvg',
        type: 'number',
        headerName: 'Your avg',
        headerClassName: 'ho-header',
        width: 95,
        renderCell: ({ row }) => (
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.myAvg > row.par ? DS.loss : DS.text, ...numericSx }}>{row.myAvg}</Typography>
        ),
    },
    { field: 'field_avg', type: 'number', headerName: 'Field avg', headerClassName: 'ho-header', width: 95 },
    {
        field: 'fieldToPar',
        type: 'number',
        headerName: 'Difficulty',
        headerClassName: 'ho-header',
        width: 100,
        description: 'How the field scores against par. Higher is harder.',
        renderCell: ({ value }) => <SignedCell value={value} invert />,
    },
    {
        field: 'vsField',
        type: 'number',
        headerName: 'Vs field',
        headerClassName: 'ho-header',
        width: 95,
        description: 'Strokes gained on the field average. Positive is better.',
        renderCell: ({ value }) => <SignedCell value={value} />,
    },
    {
        field: 'vsElite',
        type: 'number',
        headerName: 'Vs elite',
        headerClassName: 'ho-header',
        width: 95,
        description: 'Strokes gained on the elite score. Positive is better.',
        renderCell: ({ value }) => <SignedCell value={value} />,
    },
    { field: 'underParPercent', type: 'number', headerName: 'Under par', headerClassName: 'ho-header', width: 95, valueFormatter: (v) => `${v}%` },
    {
        field: 'aces',
        type: 'number',
        headerName: 'Aces',
        headerClassName: 'ho-header',
        width: 75,
        renderCell: ({ value }) => (
            <Typography sx={{ fontSize: '13px', fontWeight: value ? 800 : 400, color: value ? DS.gold : DS.textFaint, ...numericSx }}>{value}</Typography>
        ),
    },
];

const tableSx = {
    height: 560,
    width: '100%',
    borderTop: `1px solid ${DS.border}`,
    '& .ho-header': {
        color: DS.textMuted,
        fontSize: '11px !important',
        fontWeight: '700 !important',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    '& .MuiDataGrid-root': { border: 'none', background: 'transparent' },
    '& .MuiDataGrid-cell': { borderColor: DS.border, display: 'flex', alignItems: 'center', fontSize: '13px', ...numericSx },
    '& .MuiDataGrid-row': { cursor: 'pointer', '&:hover': { background: `${alpha(DS.brand, 0.06)} !important` } },
    '& .MuiDataGrid-footerContainer': { borderColor: DS.border },
    '& .MuiDataGrid-toolbarContainer': { px: 2, pt: 1.5, pb: 1, borderBottom: `1px solid ${DS.border}` },
    '& .MuiDataGrid-container--top [role="row"]': { background: `${DS.surface} !important` },
    '& .MuiTablePagination-root': { color: DS.textMuted },
};

/*****************************************************************************
 ** Page
*****************************************************************************/

const Holes = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { holeData, holeSummaryData, courseSummaryData } = context || {};
    const [selected, setSelected] = useState<HoleRow | null>(null);

    const holes = useMemo(() => toHoleRows(holeData ?? []), [holeData]);
    const courses = useMemo(() => orderBy(toCourseRows(values(courseSummaryData ?? {})), 'vsField', 'desc'), [courseSummaryData]);
    const distribution = useMemo(() => getScoreDistribution(holeData ?? []), [holeData]);
    const ranked = useMemo(() => rankableHoles(holes), [holes]);
    const rankedNote = ranked.length < holes.length ? ` · played ${MIN_RANKED_HOLE_PLAYS}+ times` : '';
    const bestHoles = useMemo(() => orderBy(ranked, 'vsField', 'desc').slice(0, 6), [ranked]);
    const worstHoles = useMemo(() => orderBy(ranked, 'vsField', 'asc').slice(0, 6), [ranked]);

    const s = holeSummaryData ?? {};
    const vsField: number = s.overall_vs_field ?? 0;
    const totalAces = sumBy(holes, 'aces');

    return (
        <RouteWrapper>
            <PageHeader
                overline="Holes"
                title="Your course book"
                subtitle="How you score on every hole and course this season, compared with the field and elite players."
            />

            {/* Overview */}
            <Panel delay={0.05} noPadding>
                <Box sx={{
                    p: { xs: 2, sm: 3 },
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: '1.4fr repeat(4, 1fr)' },
                    gap: { xs: 2.5, md: 3 },
                }}>
                    <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                        <Overline>Strokes vs field</Overline>
                        <Typography sx={{ fontSize: '34px', fontWeight: 800, color: trendColor(vsField), lineHeight: 1.15, ...numericSx }}>
                            {formatSigned(vsField, 1)}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>
                            {vsField >= 0 ? 'better' : 'worse'} than the field over one round of each hole
                        </Typography>
                    </Box>
                    <Box>
                        <Overline>Holes played</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, ...numericSx }}>{holes.length}</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>different holes</Typography>
                    </Box>
                    <Box>
                        <Overline>Scores recorded</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, ...numericSx }}>{distribution.total}</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>holes completed</Typography>
                    </Box>
                    <Box>
                        <Overline>Under par</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, ...numericSx }}>{distribution.underParPercent}%</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>birdie or better</Typography>
                    </Box>
                    <Box>
                        <Overline>Aces</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: totalAces ? DS.gold : DS.text, ...numericSx }}>{totalAces}</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>holes in one</Typography>
                    </Box>
                </Box>
            </Panel>

            {/* Scoring breakdown */}
            <Panel title="Scoring breakdown" subtitle="Every hole score this season, relative to par" grid={{ xs: 12, md: 7 }} delay={0.1}>
                <DistributionBar distribution={distribution} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(6, 1fr)' }, gap: 1.5, mt: 2.5 }}>
                    {distribution.buckets.map(b => (
                        <Box key={b.key}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: BUCKET_COLORS[b.key] }} />
                                <Overline>{b.label}</Overline>
                            </Box>
                            <Typography sx={{ fontSize: '22px', fontWeight: 800, color: DS.text, lineHeight: 1.3, ...numericSx }}>{b.percent}%</Typography>
                            <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>{b.count}</Typography>
                        </Box>
                    ))}
                </Box>
            </Panel>

            {/* Benchmarks */}
            <Panel title="How you compare" subtitle="Your average on each hole against three benchmarks" grid={{ xs: 12, md: 5 }} delay={0.15}>
                <CompareRow
                    label="Par or better"
                    better={s.count_better_than_par ?? 0}
                    worse={s.count_worse_than_par ?? 0}
                    color={DS.gain}
                    betterText="Average at or under par"
                />
                <CompareRow
                    label="Field average or better"
                    better={s.count_better_than_avg ?? 0}
                    worse={s.count_worse_than_avg ?? 0}
                    color={DS.info}
                    betterText="Beating the typical player"
                />
                <CompareRow
                    label="Better than elite"
                    better={s.count_better_than_elite ?? 0}
                    worse={s.count_worse_than_elite ?? 0}
                    color={DS.gold}
                    betterText="Beating the elite score"
                />
            </Panel>

            {/* Best / worst holes */}
            <HoleList title="Best holes" subtitle={`Most strokes gained on the field average${rankedNote}`} holes={bestHoles} onOpen={setSelected} delay={0.1} />
            <HoleList title="Toughest holes" subtitle={`Most strokes lost to the field average${rankedNote}`} holes={worstHoles} onOpen={setSelected} delay={0.15} />

            {/* Courses */}
            <Panel
                title="Courses"
                subtitle="Only courses where you've played every hole this season. Your score is your average on each hole added up."
                delay={0.15}
            >
                {courses.length === 0 ? (
                    <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>Play every hole of a course to see it here.</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5 }}>
                        {courses.map((course, i) => <CourseCard key={course.id} course={course} delay={0.15 + Math.min(i, 10) * 0.03} />)}
                    </Box>
                )}
            </Panel>

            {/* All holes */}
            <Panel title="All holes" subtitle="Click a hole to see every score you've made on it" delay={0.2} noPadding>
                <Box sx={tableSx}>
                    <DataGrid
                        rows={holes}
                        columns={columns}
                        density="compact"
                        rowHeight={44}
                        sortingOrder={['desc', 'asc']}
                        slots={{ toolbar: DataTableToolbarWithExportAndSearch }}
                        onRowClick={({ row }) => setSelected(row)}
                        initialState={{
                            columns: { columnVisibilityModel: { course: false, underParPercent: false } },
                            pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            sorting: { sortModel: [{ field: 'timesPlayed', sort: 'desc' }] },
                        }}
                        sx={{ border: 0 }}
                    />
                </Box>
            </Panel>

            <Grid item xs={12} sx={{ mb: 6 }} />

            <HoleDialog hole={selected} onClose={() => setSelected(null)} />
        </RouteWrapper>
    );
};

export default Holes;
